import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Skeleton } from '@material-ui/lab';
import getDocumentColumns from './constants/columns';
import AdminLayout from 'components/layout/AdminLayout';
import Header from 'components/layout/Partials/Header';
import { Helmet } from 'react-helmet';
import Section from 'components/UI/Section';
import { Box, Grid } from '@material-ui/core';
import Upload from './Upload';
import api from 'api';
import { ToastsStore } from 'react-toasts';
import { hideDialog, showDialog } from 'redux/alerts/actions';
import { useDispatch } from 'react-redux';
import YesNoActions from 'catalyst/core/Dialog/actions/YesNoActions';

const tableSkeleton = () => (
    <div style={{ width: '100%' }}>
        {Array.from({ length: 8 }).map((value) => (
            <Skeleton key={value} style={{ height: 50, width: '100%' }} />
        ))}
    </div>
);

const UploadManager = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();

    const fetchFiles = () => {
        api.jsr
            .allFiles()
            .then((response) => {
                setData(response);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };

    const addFiles = (files) => {
        setData([...files, ...data]);
        handleErrors(files);
    };

    const handleErrors = (files) => {
        const hasErrors = files.some((file) => file.errorMessage != null);
        if (hasErrors) {
            ToastsStore.error('Some of the files uploaded contained errors');
        } else {
            ToastsStore.success('Files uploaded and processed successfully');
        }
    };

    const markRowAsProcessing = (row) => {
        const files = data.map((file) =>
            file._id === row._id ? { ...file, processing: true } : file
        );
        setData(files);
    };

    const replaceFile = (row) => {
        const files = data.map((file) => (file._id === row._id ? row : file));
        setData(files);
    };

    const removeFile = (row) => {
        const files = data.filter((file) => file._id != row._id);
        setData(files);
    };

    const retryProcessPdf = (row) => {
        markRowAsProcessing(row);
        api.jsr
            .processFile({ fileId: row._id })
            .then((file) => {
                const { analyzerStatus } = file;
                if (analyzerStatus === 'error') {
                    ToastsStore.error(
                        'Some went wrong processing you file, please try again'
                    );
                } else {
                    ToastsStore.success('File processed successfully');
                }
                replaceFile(file);
            })
            .catch((err) => {
                console.error(err);
                replaceFile(row);
                ToastsStore.error(
                    'Something went wrong processing your file, please try again.'
                );
            });
    };

    const closeDialog = () => {
        dispatch(hideDialog());
    };

    const deleteFile = (row) => {
        closeDialog();
        api.jsr
            .deleteFile(row._id)
            .then(() => {
                removeFile(row);
                ToastsStore.success('File deleted successfully');
            })
            .catch((err) => {
                console.error(err);
                ToastsStore.error(
                    'Something went wrong deleting the file, please try again.'
                );
            });
    };

    const handleDeleteFile = (row) => {
        dispatch(
            showDialog({
                open: true,
                title: `Delete file`,
                description: `Are you sure you want to delete ${row.file.originalname}? 
                This will delete all its associated records and the file from storage.`,
                dialogActions: (
                    <YesNoActions
                        confirm={() => deleteFile(row)}
                        decline={closeDialog}
                        confirmText="Delete"
                    />
                ),
                handleClose: closeDialog,
            })
        );
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const documentColumns = getDocumentColumns(
        retryProcessPdf,
        handleDeleteFile
    );

    return (
        <AdminLayout>
            <Helmet>
                <title>Upload Manager</title>
            </Helmet>
            <div className="o-page">
                <Header>
                    <h2 className="c-navbar__title">Upload Manager</h2>
                </Header>
                <div className="u-m-xsmall">
                    <Section>
                        <Box className="u-ph-small u-pv-large u-mb-large full_height">
                            <Box p={2}>
                                <Upload addFiles={addFiles} />
                            </Box>
                            <div className="c-table-responsive@wide">
                                <DataTable
                                    title="Uploaded Files (last 7 days)"
                                    columns={documentColumns}
                                    data={data}
                                    pagination
                                    progressComponent={tableSkeleton()}
                                    defaultSortField="name"
                                    progressPending={loading}
                                />
                            </div>
                        </Box>
                    </Section>
                </div>
            </div>
        </AdminLayout>
    );
};

export default UploadManager;
